import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  PointerEvent as ReactPointerEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EverwatchPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Everwatch" (/everwatch) — an EXPIRATION paywall, built to the standing
// #paywalls directive: SECURITY & PRIVACY.
//
// The mechanic no other paywall here uses: a WATCH RAIL you raise by hand. Your
// Premium watch has lapsed, so the vault's guard sits at STANDBY at the bottom
// of a tall illuminated rail. You DRAG a physical guard handle up the rail (or
// tap / use the keyboard) and, station by station, each real protection ignites
// as the fill crosses it — an armored lattice weaving up over a drifting
// filmstrip of your memories — until the vault is back at FULL WATCH. Drag it
// back down and the guard honestly recedes: direct-manipulation contrast
// framing you control with your thumb.
//
// This is deliberately NOT a split-flap board (Solari), not a ring drawn closed
// (Halo), not an opening heirloom (Locket), not a dial / keypad / vault door /
// orbiting galaxy. The interaction model is CONTINUOUS DRAG along a vertical
// gauge — commitment + progress-completion — and the register is a calm,
// cinematic "raise the watch" rather than a mechanical console. Value framing is
// loss-aversion done HONESTLY: your memories stayed locked and visible the whole
// time; the free vault is still private; raising the watch only re-arms the
// EXTRA Premium guard layers — nothing was ever lost, deleted or exposed.
//
// App-Store compliant: featured, most-prominent price is the YEARLY plan (7-day
// free trial, per-month broken out, honest Save %); monthly is a small secondary
// line. No scarcity / countdown, no fake urgency, explicit no-data-loss. Reuses
// only the house font, colour tokens and the bundled byheart memory photos. Full
// light + dark, reduced-motion aware. Renders standalone on debug data; no
// native injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories drifting behind the armored glass — reused bundled byheart
// photos, the only assets this paywall pulls in.
const MEMORIES = ["m02", "m06", "m11", "m04", "m13", "m08", "m15"].map(photo)

// Six REAL Premium protections, seated as stations along the rail from the
// bottom (first to arm) to the top (last to arm). `at` is the fill level, 0..1,
// at which the station ignites.
type Station = {
  id: string
  label: string
  hint: string
  status: string
  at: number
  icon: JSX.Element
}

const STATIONS: Station[] = [
  {
    id: "lock",
    label: "App lock",
    hint: "Face ID or PIN — only you get in",
    status: "ARMED",
    at: 0.1,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M7.8 10V7.7a4.2 4.2 0 0 1 8.4 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <rect
          x="5.4"
          y="10"
          width="13.2"
          height="9.2"
          rx="2.5"
          fill="currentColor"
        />
        <circle cx="12" cy="14.2" r="1.3" fill="var(--ew-ink)" />
      </svg>
    ),
  },
  {
    id: "backup",
    label: "Encrypted backup",
    hint: "A safe, private copy kept for you",
    status: "SEALED",
    at: 0.26,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M7 18a4 4 0 0 1-.6-7.96 5 5 0 0 1 9.7-1.2A3.8 3.8 0 0 1 18.5 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.4 13.6l1.9 1.9 3.5-3.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "sync",
    label: "Cloud sync",
    hint: "Every device, in step",
    status: "SYNCED",
    at: 0.42,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "breakin",
    label: "Break-in alerts",
    hint: "Catch a snoop in the act",
    status: "ON WATCH",
    at: 0.58,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.4-3 7.4-7 8.7-4-1.3-7-4.3-7-8.7v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.4v4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
        <circle cx="12" cy="15.4" r="1.05" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "decoy",
    label: "Decoy vault",
    hint: "A false door for prying eyes",
    status: "READY",
    at: 0.74,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <rect
          x="4.4"
          y="6.2"
          width="12"
          height="12"
          rx="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <rect
          x="8.4"
          y="3.2"
          width="11.2"
          height="11.2"
          rx="2.1"
          fill="var(--ew-glass)"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    ),
  },
  {
    id: "adfree",
    label: "Ad-free vault",
    hint: "Just your memories, nothing else",
    status: "CLEAR",
    at: 0.9,
    icon: (
      <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
        <circle
          cx="12"
          cy="12"
          r="8.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M6.6 6.6l10.8 10.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

const FULL = 1
const clamp = (n: number) => (n < 0 ? 0 : n > FULL ? FULL : n)

function EverwatchPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // level: the watch fill, 0 (STANDBY) → 1 (FULL WATCH). Continuous.
  const [level, setLevel] = useState(0)
  const [dragging, setDragging] = useState(false)
  const railRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const userTouched = useRef(false)

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#0d1017" : "#eef1f6")
    actions.setFooterInsetColor(darkMode ? "#0f1219" : "#ffffff")
  }, [actions, darkMode])

  // On load the watch auto-raises from STANDBY to FULL over ~1.4s so the payoff
  // reads within ~3s. Reduced motion arrives already at full watch. Any user
  // touch cancels the auto-raise and hands control to the thumb.
  useEffect(() => {
    if (reduce) {
      setLevel(FULL)
      return
    }
    const DURATION = 1400
    const DELAY = 380
    let start = 0
    const ease = (t: number) => 1 - Math.pow(1 - t, 3) // easeOutCubic
    const step = (ts: number) => {
      if (userTouched.current) return
      if (!start) start = ts
      const t = clamp((ts - start - DELAY) / DURATION)
      if (t < 0) {
        rafRef.current = requestAnimationFrame(step)
        return
      }
      setLevel(ease(t))
      if (t < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [reduce])

  const stopAuto = useCallback(() => {
    userTouched.current = true
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
  }, [])

  const levelFromClientY = useCallback((clientY: number) => {
    const rail = railRef.current
    if (!rail) return 0
    const r = rail.getBoundingClientRect()
    return clamp((r.bottom - clientY) / r.height)
  }, [])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    stopAuto()
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
    setLevel(levelFromClientY(e.clientY))
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    setLevel(levelFromClientY(e.clientY))
  }
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    setDragging(false)
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* pointer already released */
    }
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null
    if (e.key === "ArrowUp" || e.key === "ArrowRight")
      next = clamp(level + 0.16)
    else if (e.key === "ArrowDown" || e.key === "ArrowLeft")
      next = clamp(level - 0.16)
    else if (e.key === "Home") next = 0
    else if (e.key === "End") next = FULL
    if (next != null) {
      e.preventDefault()
      stopAuto()
      setLevel(next)
    }
  }

  // Tapping a protection chip raises the watch to at least that station.
  const raiseTo = (at: number) => {
    stopAuto()
    setLevel((cur) => Math.max(cur, Math.min(FULL, at + 0.03)))
  }

  const armedCount = STATIONS.filter((s) => level >= s.at).length
  const full = armedCount === STATIONS.length
  // The station most recently crossed — drives the floating callout.
  const active = useMemo(() => {
    let a: Station | null = null
    for (const s of STATIONS) if (level >= s.at) a = s
    return a
  }, [level])
  const pct = Math.round((armedCount / STATIONS.length) * 100)

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_everwatch_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Raise my vault to full watch"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  const fillPct = `${(level * 100).toFixed(2)}%`

  return (
    <div className={`${styles.root} ${darkMode ? styles.isDark : ""}`}>
      <div className={styles.aura} aria-hidden data-full={full} />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--ew-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker} data-full={full}>
            <i className={styles.kickDot} />
            {full ? "Full watch restored" : "Premium watch on standby"}
          </span>
          <h1 className={styles.title}>Raise your vault back to full watch.</h1>
          <p className={styles.sub}>
            Your memories stayed locked and yours the whole time. Slide the
            guard up to re-arm the extra Premium watch over them.
          </p>
        </header>
      </div>

      {/* ── The watch stage: armored viewport + guard rail ──────────────── */}
      <div className={styles.stage}>
        <div className={styles.viewport} data-full={full}>
          {/* Drifting filmstrip of memories, always visible (never hidden). */}
          <div className={styles.film} aria-hidden>
            <div className={styles.filmTrack}>
              {[...MEMORIES, ...MEMORIES].map((src, i) => (
                <span
                  key={i}
                  className={styles.frame}
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
            </div>
          </div>

          {/* Armored lattice — the ADDED guard layer, woven over (not hiding)
              the memories, intensifying with the watch level. */}
          <div
            className={styles.lattice}
            style={{ opacity: 0.12 + level * 0.62 }}
            aria-hidden
          />
          <div className={styles.sheen} aria-hidden />

          {/* Watch status chip */}
          <div className={styles.statusChip} data-full={full}>
            <span className={styles.statusPip} />
            {full ? "FULL WATCH" : level <= 0.02 ? "STANDBY" : "RAISING"}
          </div>

          {/* Live readout */}
          <div className={styles.readout} aria-live="polite">
            <span className={styles.readCount}>
              {armedCount}
              <em>/ {STATIONS.length}</em>
            </span>
            <span className={styles.readLabel}>guards on watch</span>
          </div>

          {/* ── The guard rail (drag me) ──────────────────────────────── */}
          <div
            ref={railRef}
            className={`${styles.rail} ${dragging ? styles.railDrag : ""}`}
            role="slider"
            tabIndex={0}
            aria-label="Vault watch level"
            aria-valuemin={0}
            aria-valuemax={STATIONS.length}
            aria-valuenow={armedCount}
            aria-valuetext={`${armedCount} of ${STATIONS.length} guards on watch`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
          >
            <div className={styles.railTrack}>
              <div className={styles.railFill} style={{ height: fillPct }} />
              {STATIONS.map((s) => {
                const on = level >= s.at
                return (
                  <span
                    key={s.id}
                    className={`${styles.tick} ${on ? styles.tickOn : ""}`}
                    style={{ bottom: `${s.at * 100}%` }}
                  >
                    <span className={styles.tickDot} />
                  </span>
                )
              })}
            </div>

            {/* The guard head rides the top of the fill and casts the sweep. */}
            <div className={styles.head} style={{ bottom: fillPct }}>
              <span className={styles.headSweep} data-full={full} aria-hidden />
              <span className={styles.headGrip}>
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                  <path
                    d="M7 14l5-5 5 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 18l5-5 5 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.45"
                  />
                </svg>
              </span>
            </div>

            {/* Floating callout naming the station being crossed. */}
            <div
              className={styles.callout}
              style={{ bottom: fillPct }}
              data-standby={level <= 0.02}
            >
              <span className={styles.calloutStatus}>
                {full ? "ALL CLEAR" : active ? active.status : "STANDBY"}
              </span>
              <span className={styles.calloutName}>
                {full
                  ? "Every guard on watch"
                  : active
                    ? active.label
                    : "Guard resting — slide up"}
              </span>
            </div>

            {!userTouched.current && !reduce && !full && (
              <span className={styles.dragHint} aria-hidden>
                drag up
              </span>
            )}
          </div>
        </div>

        {/* Protection legend — ignites station by station with the rail. */}
        <div className={styles.legend}>
          {STATIONS.map((s) => {
            const on = level >= s.at
            return (
              <button
                key={s.id}
                type="button"
                className={`${styles.chip} ${on ? styles.chipOn : ""}`}
                onClick={() => raiseTo(s.at)}
                title={s.hint}
              >
                <span className={styles.chipIcon}>{s.icon}</span>
                <span className={styles.chipText}>
                  <span className={styles.chipName}>{s.label}</span>
                  <span className={styles.chipHint}>
                    {on ? s.status : "standby"}
                  </span>
                </span>
                <span className={styles.chipLamp} data-on={on} aria-hidden />
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 20 }}>
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase} data-full={full}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Your vault stays locked and private on the free plan too — nothing is
          ever deleted or locked away, and you always hold the only key. Raising
          the watch simply re-arms the extra Premium guard over your memories.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
        <p className={styles.watchMeta} aria-hidden>
          Watch level {pct}%
        </p>
      </div>
    </div>
  )
}

export default EverwatchPaywall
